// Los avisos en este aparato (04-11, Pau: «no recibo notificaciones en la web ni en el pc ni nada cuando santi me
// escribe»). Ningún aparato se había apuntado nunca a los avisos: la única forma era /settings, el panel viejo que Pau
// no usa. Aquí, dentro de la oficina y solo para la sesión de Pau (los avisos de Santi van a sus aparatos), un aviso
// tranquilo que se activa con un clic:
//  - `oficina`: flotando arriba de la oficina al abrirla, si el navegador aún no ha preguntado (permiso «default», o
//    concedido pero sin apuntar) y no hay ninguna ventana encima. Lo bloqueado no se dice aquí, solo tras pulsar.
//  - `santi`: una franja arriba de la ventana «Santi», que además explica lo bloqueado (cómo permitirlo, sin jerga) y,
//    en el iPad o el iPhone sin la oficina instalada, que hay que añadirla a la pantalla de inicio (en la pestaña del
//    navegador no hay avisos).
// «Activar» es el gesto que exige el navegador (iOS sobre todo): llama a subscribeToPush (lib/push.ts) y confirma
// «Avisos activados en este ordenador.». La ✕ lo quita en este aparato para siempre (localStorage). No sale si el
// servidor no puede mandar avisos (pushEnabledOnServer). Volver a apuntar un aparato ya apuntado lo hace main.tsx al
// arrancar (resyncPushSubscription), no esto.
import { Bell, BellOff, Check, X, type LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { t } from '../../../i18n';
import { getCurrentSubscription, pushEnabledOnServer, pushSupport, subscribeToPush } from '../../../lib/push';
import { cn } from '../../../lib/utils';
import { useSinConexion } from '../AvisoSinConexion';
import { MARGEN_PX, useIzquierdaPx } from '../marco/Escenario';
import { useHablaComoPau } from '../pelotas/store';
import { Boton, BotonIcono } from '../ui';

/**
 * `activados`: recién activados (la confirmación). `activos`, `sinSoporte`, `sinServidor`: solo los dice la hoja «Avisos
 * y sonido» (HojaAvisos); la franja, en esos casos, calla (`nada`).
 */
export type EstadoAvisos = 'nada' | 'pedir' | 'activando' | 'activados' | 'bloqueados' | 'instalar' | 'fallo' | 'activos' | 'sinSoporte' | 'sinServidor';
export type Aparato = 'ordenador' | 'ipad' | 'iphone';
/** El sistema, para decir con qué sonido suena SU aviso (eso no se elige desde la web). */
export type Sistema = 'windows' | 'mac' | 'ipad' | 'iphone' | 'otro';

/** La ✕, recordada en este aparato. */
export const CLAVE_QUITADO = 'oficina:avisos-aparato:quitado';
/** Cuánto se queda «Avisos activados en este ordenador.» antes de irse solo. */
export const CONFIRMACION_MS = 6_000;

/** Qué aparato es, para decirlo con su nombre. El iPad moderno se presenta como un Mac: lo delata la pantalla táctil. */
export function aparatoActual(nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> = navigator): Aparato {
  const ua = nav.userAgent || '';
  if (/iPhone|iPod/.test(ua)) return 'iphone';
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && (nav.maxTouchPoints ?? 0) > 1)) return 'ipad';
  return 'ordenador';
}

export function sistemaActual(nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> = navigator): Sistema {
  const aparato = aparatoActual(nav);
  if (aparato !== 'ordenador') return aparato;
  const ua = nav.userAgent || '';
  if (/Windows/.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/.test(ua)) return 'mac';
  return 'otro';
}

/** La oficina abierta desde la pantalla de inicio (instalada), no en una pestaña del navegador. */
export function enPantallaDeInicio(): boolean {
  try {
    if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  } catch {
    // Navegador sin matchMedia: se mira lo de Safari.
  }
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** Cómo están los avisos de este aparato, sin que Pau haya pulsado nada (la hoja lo dice todo). */
export async function estadoDelAparato(aparato: Aparato): Promise<EstadoAvisos> {
  const soporte = pushSupport();
  // En el iPad y el iPhone, los avisos solo existen con la oficina instalada.
  if (soporte === 'unsupported') return aparato !== 'ordenador' && !enPantallaDeInicio() ? 'instalar' : 'sinSoporte';
  if (soporte === 'denied') return 'bloqueados';
  if (Notification.permission === 'granted' && (await getCurrentSubscription())) return 'activos';
  return (await pushEnabledOnServer()) ? 'pedir' : 'sinServidor';
}

/** Lo que dice la franja al abrir: lo mismo, salvo que calla cuando ya están o cuando Pau no puede hacer nada. */
export async function estadoAlAbrir(aparato: Aparato): Promise<EstadoAvisos> {
  const e = await estadoDelAparato(aparato);
  return e === 'activos' || e === 'sinSoporte' || e === 'sinServidor' ? 'nada' : e;
}

function leerQuitado(): boolean {
  try {
    return localStorage.getItem(CLAVE_QUITADO) === '1';
  } catch {
    return false;
  }
}

/**
 * El estado de los avisos de este aparato y lo que se puede hacer con ellos. Sin `activo`, no mira nada. `hoja`: la de
 * «Avisos y sonido», que lo dice todo (también lo activo) y no hace caso de la ✕ de la franja.
 */
export function useAvisosAparato(activo: boolean, modo: 'franja' | 'hoja' = 'franja') {
  const aparato = useMemo(() => aparatoActual(), []);
  const [estado, setEstado] = useState<EstadoAvisos>('nada');
  const [quitado, setQuitado] = useState(leerQuitado);
  // Pulsó «Activar» en esta visita: lo que pase después (bloqueado, fallo) se dice también arriba de la oficina.
  const [pulsado, setPulsado] = useState(false);

  const hoja = modo === 'hoja';
  useEffect(() => {
    if (!activo || (quitado && !hoja)) return;
    let vivo = true;
    void (hoja ? estadoDelAparato(aparato) : estadoAlAbrir(aparato)).then((e) => {
      if (vivo) setEstado(e);
    });
    return () => {
      vivo = false;
    };
  }, [activo, aparato, quitado, hoja]);

  useEffect(() => {
    if (estado !== 'activados') return;
    const id = setTimeout(() => setEstado(hoja ? 'activos' : 'nada'), CONFIRMACION_MS);
    return () => clearTimeout(id);
  }, [estado, hoja]);

  const activar = useCallback(async () => {
    setPulsado(true);
    setEstado('activando');
    if (await subscribeToPush()) {
      setEstado('activados');
      return;
    }
    const permiso = typeof Notification === 'undefined' ? 'default' : Notification.permission;
    setEstado(permiso === 'denied' ? 'bloqueados' : 'fallo');
  }, []);

  const quitar = useCallback(() => {
    // Lo activado ya no vuelve a salir (está apuntado): no hace falta recordarlo, y si algún día se pierde, que avise.
    if (estado !== 'activados') {
      try {
        localStorage.setItem(CLAVE_QUITADO, '1');
      } catch {
        // Sin almacenamiento: se quita durante esta visita.
      }
    }
    setQuitado(true);
  }, [estado]);

  return { aparato, estado: quitado && !hoja ? ('nada' as const) : estado, pulsado, activar, quitar };
}

export function textoDe(estado: EstadoAvisos, aparato: Aparato): string {
  const p = { aparato: t(`oficinaV2.avisosAparato.aparato.${aparato}`) };
  switch (estado) {
    case 'activados':
      return t('oficinaV2.avisosAparato.activados', p);
    case 'fallo':
      return t('oficinaV2.avisosAparato.fallo', p);
    case 'bloqueados':
      return aparato === 'ordenador' ? t('oficinaV2.avisosAparato.bloqueadosNavegador') : t('oficinaV2.avisosAparato.bloqueadosApp', p);
    case 'instalar':
      return t('oficinaV2.avisosAparato.instalar', p);
    case 'activos':
      return t('oficinaV2.avisosAparato.hoja.activos', p);
    case 'sinSoporte':
      return t('oficinaV2.avisosAparato.hoja.sinSoporte');
    case 'sinServidor':
      return t('oficinaV2.avisosAparato.hoja.sinServidor');
    default:
      return t('oficinaV2.avisosAparato.pedir', p);
  }
}

export const ICONO: Record<EstadoAvisos, LucideIcon> = {
  nada: Bell,
  activos: Check,
  sinSoporte: BellOff,
  sinServidor: BellOff,
  pedir: Bell,
  activando: Bell,
  fallo: Bell,
  instalar: Bell,
  activados: Check,
  bloqueados: BellOff,
};

/**
 * Lo que se ve, sin estado propio (lo pinta ActivarAvisos y, en cada estado, la historia de Storybook). `oficina`: una
 * cápsula flotante con la acción en oro (es lo único que pide actuar ahí). `santi`: una fila gris como la de lo que no
 * llegó (AvisosActo), con la acción en blanco (el oro de la ventana es solo «Enviar»).
 */
export function FranjaAvisos({ donde, estado, aparato, alActivar, alQuitar }: { donde: 'oficina' | 'santi'; estado: EstadoAvisos; aparato: Aparato; alActivar: () => void; alQuitar: () => void }) {
  const Icono = ICONO[estado];
  const conAccion = estado === 'pedir' || estado === 'activando' || estado === 'fallo';
  return (
    <div
      role="status"
      className={cn(
        'flex items-center gap-3',
        donde === 'santi'
          ? 'rounded-o-m bg-oficina-control/60 py-1 pl-3 pr-1'
          : 'pointer-events-auto max-w-[720px] rounded-o-xl bg-oficina-elevada py-1.5 pl-4 pr-1.5 shadow-o-flota ring-1 ring-white/10 animate-in fade-in-0 duration-300 ease-o-apple',
      )}
      data-testid="avisos-aparato"
      data-estado={estado}
      data-donde={donde}
    >
      <Icono className={cn('h-[18px] w-[18px] shrink-0', estado === 'activados' ? 'text-oficina-trabajando' : 'text-oficina-texto2')} aria-hidden />
      <p className={cn('min-w-0 flex-1', donde === 'santi' ? 'py-2.5 text-o-cuerpo text-oficina-texto2' : 'py-1 text-o-texto text-oficina-texto')} data-testid="avisos-aparato-texto">
        {textoDe(estado, aparato)}
      </p>
      {conAccion ? (
        <Boton
          variante={donde === 'oficina' ? 'primario' : 'fantasma'}
          tamano={donde === 'oficina' ? 'sm' : 'md'}
          cargando={estado === 'activando'}
          onClick={alActivar}
          className={donde === 'santi' ? 'px-3.5 text-oficina-texto' : undefined}
          data-testid="avisos-aparato-activar"
        >
          {t('oficinaV2.avisosAparato.activar')}
        </Boton>
      ) : null}
      <BotonIcono icono={X} tamano={donde === 'oficina' ? 'sm' : 'md'} etiqueta={t('oficinaV2.avisosAparato.quitar')} onClick={alQuitar} data-testid="avisos-aparato-quitar" />
    </div>
  );
}

/**
 * El aviso, donde toca. `oficina`: flota arriba, centrado en el hueco de las ventanas como el de «sin conexión» (o en su
 * sitio, en el móvil); solo lo que se puede activar (y lo que pasa tras pulsar). `santi`: una franja arriba de la
 * ventana, con todos los estados (también lo bloqueado y, en el iPad, que hay que instalarla).
 */
export function ActivarAvisos({ donde, movil = false }: { donde: 'oficina' | 'santi'; movil?: boolean }) {
  const comoPau = useHablaComoPau();
  const { aparato, estado, pulsado, activar, quitar } = useAvisosAparato(comoPau);
  const sinConexion = useSinConexion();
  const izquierda = useIzquierdaPx();
  const visible =
    comoPau &&
    estado !== 'nada' &&
    (donde === 'santi' || ((estado !== 'bloqueados' || pulsado) && estado !== 'instalar' && !sinConexion));
  if (!visible) return null;
  const franja = <FranjaAvisos donde={donde} estado={estado} aparato={aparato} alActivar={() => void activar()} alQuitar={quitar} />;
  if (donde === 'santi') return <div className="border-b border-oficina-linea bg-oficina-elevada px-4 py-2">{franja}</div>;
  return (
    <div
      className={movil ? 'flex shrink-0 justify-center px-4 pt-2' : 'pointer-events-none fixed top-[calc(0.5rem+env(safe-area-inset-top))] z-[55] flex justify-center px-4'}
      style={movil ? undefined : { left: izquierda, right: MARGEN_PX }}
    >
      {franja}
    </div>
  );
}
